// Alert Log Service — Thin client for Sequence Detect alert log history
// Delegates WebSocket communication to DashboardService (shared, authenticated connection)

import { dashboardService } from './dashboard';
import type { AlertLogEntry, AlertMetricType } from '../components/sequenceDetect/AlertHistoryPanel';
import type { AlertBehaviorType, AlertBehaviorKey, FrequencyWindow } from '../components/sequenceDetect/behaviorTypes';

export interface AlertLogRow {
  id: string;
  userId: string;
  sessionId: string | null;
  behaviorKey: AlertBehaviorKey;
  behaviorType: AlertBehaviorType;
  severity: 'warning' | 'critical';
  metricType: AlertMetricType;
  title: string;
  message: string;
  thresholdValue: number;
  actualValue: number;
  frequencyWindow: FrequencyWindow | null;
  detectedAt: number; // epoch ms
  createdAt: number;  // epoch ms
}

class AlertLogService {
  /**
   * Send a request through the DashboardService's shared WebSocket connection.
   * DashboardService handles authentication, reconnection, and message routing.
   */
  private send(method: string, params?: unknown): Promise<unknown> {
    return dashboardService.send(method, params);
  }

  // ─── Alert Log CRUD ────────────────────────────────────────────────────────

  async listAlertLogs(query: {
    sessionId?: string;
    severity?: 'warning' | 'critical';
    behaviorKey?: AlertBehaviorKey;
    from?: number;  // epoch ms
    to?: number;    // epoch ms
    limit?: number;
    offset?: number;
  }): Promise<AlertLogEntry[]> {
    try {
      const result = (await this.send('dashboard:alertLog:list', query)) as any;
      if (result?.success && result.alerts) {
        return result.alerts.map(parseAlertLogRow);
      }
      return [];
    } catch (err) {
      console.error('[AlertLogService] Failed to list alert logs:', err);
      return [];
    }
  }

  async getAlertLog(id: string): Promise<AlertLogEntry | null> {
    try {
      const result = (await this.send('dashboard:alertLog:get', { id })) as any;
      if (result?.success && result.alert) {
        return parseAlertLogRow(result.alert);
      }
      return null;
    } catch (err) {
      console.error('[AlertLogService] Failed to get alert log:', err);
      return null;
    }
  }

  async insertAlertLog(entry: {
    sessionId?: string;
    behaviorKey: AlertBehaviorKey;
    behaviorType: AlertBehaviorType;
    severity: 'warning' | 'critical';
    metricType: AlertMetricType;
    title: string;
    message: string;
    thresholdValue: number;
    actualValue: number;
    frequencyWindow?: FrequencyWindow;
    detectedAt: number; // epoch ms
  }): Promise<AlertLogEntry | null> {
    try {
      const result = (await this.send('dashboard:alertLog:insert', entry)) as any;
      if (result?.success && result.alert) {
        return parseAlertLogRow(result.alert);
      }
      return null;
    } catch (err) {
      console.error('[AlertLogService] Failed to insert alert log:', err);
      return null;
    }
  }

  async deleteAlertLogsOlderThan(timestamp: number): Promise<number> {
    try {
      const result = (await this.send('dashboard:alertLog:delete', { timestamp })) as any;
      if (result?.success) return result.deleted ?? 0;
      return 0;
    } catch (err) {
      console.error('[AlertLogService] Failed to delete old alert logs:', err);
      return 0;
    }
  }
}

/** Parse raw DB row (snake_case) → AlertLogEntry (camelCase + Date conversion) */
function parseAlertLogRow(raw: any): AlertLogEntry {
  return {
    id: raw.id,
    type: raw.behavior_type as AlertBehaviorType,
    severity: raw.severity as 'warning' | 'critical',
    title: raw.title,
    message: raw.message,
    detectedAt: new Date(raw.detected_at),
    threshold: raw.threshold_value,
    actualValue: raw.actual_value,
    metricType: raw.metric_type as AlertMetricType,
    behaviorKey: raw.behavior_key as AlertBehaviorKey,
    contactNotified: false,   // not stored yet — future phase
    acknowledged: false,      // not stored yet — future phase
  };
}

// Singleton instance
export const alertLogService = new AlertLogService();
export default alertLogService;